import { createContext, use, useCallback, useMemo, useState, type ReactNode } from 'react';
import { apiRequest } from '../lib/api';
import { clearAuthHeaders, loadAuthHeaders } from '../lib/auth-storage';
import { KPI_API } from '../lib/kpi';

export interface CurrentUser {
  email: string;
  allow_password_change: boolean;
}

export type AuthStatus = 'authenticated' | 'unauthenticated';

/** Two-factor is mandatory: a right password only ever leads to a code. */
export type LoginResult =
  | { kind: 'otp_required'; challengeToken: string }
  | { kind: 'otp_enrollment_required'; challengeToken: string };

export interface AuthContextValue {
  status: AuthStatus;
  currentUser: CurrentUser | null;
  login: (email: string, password: string) => Promise<LoginResult>;
  enrollOtp: (challengeToken: string) => Promise<{ otpauthUri: string; secret: string }>;
  confirmOtp: (challengeToken: string, code: string) => Promise<{ user: CurrentUser; recoveryCodes: string[] }>;
  verifyOtp: (challengeToken: string, code: string) => Promise<CurrentUser>;
  changePassword: (password: string, passwordConfirmation: string) => Promise<CurrentUser>;
  logout: () => Promise<void>;
}

interface SignInResponse {
  data: CurrentUser;
}

interface ChallengeResponse {
  status: LoginResult['kind'];
  challenge_token: string;
}

const AUTH = `${KPI_API}/auth`;
const USER_STORAGE_KEY = 'kpi_dashboard.user';

function loadUser(): CurrentUser | null {
  const raw = localStorage.getItem(USER_STORAGE_KEY);
  if (!raw) return null;

  try {
    const user = JSON.parse(raw) as Partial<CurrentUser>;
    if (typeof user.email !== 'string' || typeof user.allow_password_change !== 'boolean') return null;
    return user as CurrentUser;
  } catch {
    return null;
  }
}

function clearSession(): void {
  clearAuthHeaders();
  localStorage.removeItem(USER_STORAGE_KEY);
}

// A token without the user, or the other way round, is no session at all.
function initialState(): { status: AuthStatus; currentUser: CurrentUser | null } {
  const user = loadUser();
  if (loadAuthHeaders() && user) return { status: 'authenticated', currentUser: user };

  clearSession();
  return { status: 'unauthenticated', currentUser: null };
}

function toUser(body: SignInResponse): CurrentUser {
  return { email: body.data.email, allow_password_change: body.data.allow_password_change };
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [{ status, currentUser }, setState] = useState(initialState);

  const remember = useCallback((user: CurrentUser) => {
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    setState({ status: 'authenticated', currentUser: user });
    return user;
  }, []);

  const login = useCallback<AuthContextValue['login']>(async (email, password) => {
    const body = await apiRequest<ChallengeResponse>(`${AUTH}/sign_in`, { method: 'POST', body: { email, password } });
    return { kind: body.status, challengeToken: body.challenge_token };
  }, []);

  const enrollOtp = useCallback<AuthContextValue['enrollOtp']>(async (challengeToken) => {
    const body = await apiRequest<{ otpauth_uri: string; secret: string }>(`${AUTH}/otp/enroll`, {
      method: 'POST',
      body: { challenge_token: challengeToken },
    });
    return { otpauthUri: body.otpauth_uri, secret: body.secret };
  }, []);

  const confirmOtp = useCallback<AuthContextValue['confirmOtp']>(
    async (challengeToken, code) => {
      const body = await apiRequest<SignInResponse & { recovery_codes: string[] }>(`${AUTH}/otp/confirm`, {
        method: 'POST',
        body: { challenge_token: challengeToken, code },
      });
      return { user: remember(toUser(body)), recoveryCodes: body.recovery_codes };
    },
    [remember],
  );

  const verifyOtp = useCallback<AuthContextValue['verifyOtp']>(
    async (challengeToken, code) => {
      const body = await apiRequest<SignInResponse>(`${AUTH}/otp/verify`, {
        method: 'POST',
        body: { challenge_token: challengeToken, code },
      });
      return remember(toUser(body));
    },
    [remember],
  );

  // devise_token_auth's own endpoint: it lets a temporary password go without
  // the current one, and turns allow_password_change off.
  const changePassword = useCallback<AuthContextValue['changePassword']>(
    async (password, passwordConfirmation) => {
      const body = await apiRequest<SignInResponse>('/api/web/v1/auth/password', {
        method: 'PUT',
        body: { password, password_confirmation: passwordConfirmation },
      });
      return remember(toUser(body));
    },
    [remember],
  );

  const logout = useCallback<AuthContextValue['logout']>(async () => {
    try {
      await apiRequest(`${AUTH}/sign_out`, { method: 'DELETE' });
    } catch {
      // Best effort: the local session goes either way.
    }
    clearSession();
    setState({ status: 'unauthenticated', currentUser: null });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, currentUser, login, enrollOtp, confirmOtp, verifyOtp, changePassword, logout }),
    [status, currentUser, login, enrollOtp, confirmOtp, verifyOtp, changePassword, logout],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const context = use(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}
